import { NO_RISK } from '@zefr/style-guide/dist/ui/types/atrium/garm';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { SORT_MODE_COUNTRY_POPULARITY, setFormKpiKeysAtom } from '../atoms/encodedFormAtoms';
import { INCLUSION_LIST_PREFAB } from '../constants/listTypes';
import { FINAL_STEP, useDemoForm } from '../context/DemoFormContext';

export type SkipTarget = 'inclusion' | 'exclusion';

// Skip-flow defaults. These mirror the per-step defaults a user would
// click through to land on the channels results page with the broadest
// reasonable profile.
const DEFAULT_PLATFORM = 'youtube';
const DEFAULT_LANGUAGE = 'English';
const DEFAULT_COUNTRY = 'United States';
// formParamsAtom's `nextStep` calculation requires kpiKeys to be set
// before the form can advance past step 2. The specific KPI doesn't
// matter for the skip flow — any single key unlocks the gate.
const DEFAULT_KPI_KEY = 'suitability';

const useSkipToResults = () => {
  const {
    apiConcepts,
    setPlatform,
    setListType,
    setFormRisk,
    setFormInclusionListType,
    setFormIncludedConcepts,
    setFormExcludedConcepts,
    setFormLanguages,
    setFormCountries,
    setSortMode,
    setStep,
  } = useDemoForm();
  const setFormKpiKeys = useSetAtom(setFormKpiKeysAtom);

  // Inclusion uses every non-BSP concept; exclusion uses every BSP
  // concept. Mirrors the partition useChipDialog applies when the user
  // walks through the form normally.
  const conceptIdsByTarget = useMemo(() => {
    const all = Object.values(apiConcepts || {});
    return {
      inclusion: all.filter((c) => !c.is_bsp_concept).map((c) => c.id),
      exclusion: all.filter((c) => c.is_bsp_concept).map((c) => c.id),
    };
  }, [apiConcepts]);

  return useCallback(
    (target: SkipTarget) => {
      setPlatform(DEFAULT_PLATFORM);
      setFormKpiKeys([DEFAULT_KPI_KEY]);
      // setListType clears risk, languages, countries, sortMode, etc.
      // before persisting the new type, so reapply each default below.
      setListType(target);
      setFormRisk(NO_RISK);
      if (target === 'inclusion') {
        setFormInclusionListType(INCLUSION_LIST_PREFAB);
        setFormIncludedConcepts(conceptIdsByTarget.inclusion);
      } else {
        setFormExcludedConcepts(conceptIdsByTarget.exclusion);
      }
      setFormLanguages([DEFAULT_LANGUAGE]);
      setFormCountries([DEFAULT_COUNTRY]);
      setSortMode(SORT_MODE_COUNTRY_POPULARITY);
      setStep(FINAL_STEP);
    },
    [
      setPlatform,
      setFormKpiKeys,
      setListType,
      setFormRisk,
      setFormInclusionListType,
      setFormIncludedConcepts,
      setFormExcludedConcepts,
      conceptIdsByTarget.inclusion,
      conceptIdsByTarget.exclusion,
      setFormLanguages,
      setFormCountries,
      setSortMode,
      setStep,
    ],
  );
};

export default useSkipToResults;
